import { BenchmarkCategory } from '@/api/generated-catch-backend/model';
import MarkdownTextTailwind from '@/components/markdown/markdown-text-tailwind';
import { motion } from 'motion/react';
import { IconAlertCircle } from '@cmpsr/components';
import { AnimatedCard } from '../../../../common/animated-card';
import HeaderTitle from './header-title/header-title';
import RiskGauge from './risk-gauge/risk-gauge';
import RiskStatusBadge from './risk-status-badge/risk-status-badge';

interface DashboardHeaderCardProps {
  name?: string;
  benchmark: BenchmarkCategory;
  currentRisk: number;
  averageRisk: number;
  methodologyUrl?: string;
  userGender?: 'male' | 'female' | null;
  onBadgeClick: () => void;
  /** Content to render below the card (e.g., tabs) */
  belowCardContent?: React.ReactNode;
  biologicalAge?: number;
  /** Whether user is eligible for a lab order */
  isEligibleForLabOrder?: boolean;
  /** Handler for lab order CTA click */
  onLabOrderClick?: () => void;
}

export default function DashboardHeaderCard({
  name,
  benchmark,
  currentRisk,
  averageRisk,
  methodologyUrl,
  userGender,
  onBadgeClick,
  belowCardContent,
  biologicalAge,
  isEligibleForLabOrder,
  onLabOrderClick,
}: DashboardHeaderCardProps) {
  const biologicalAgeText =
    biologicalAge !== undefined
      ? `Based on your latest lab results, your biological age is **${biologicalAge}** years old`
      : '';
  return (
    <div className="relative z-10 mx-auto max-w-7xl px-5 pt-3 sm:px-6 md:px-16 md:pt-10">
      <AnimatedCard>
        <div className="flex min-h-[250px] flex-col lg:flex-row lg:gap-12">
          {/* Left side content */}
          <div className="order-2 flex flex-shrink-0 flex-col items-center gap-6 lg:order-1">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <RiskGauge riskPercentage={currentRisk} populationAverage={averageRisk} />
            </motion.div>

            <RiskStatusBadge riskLevel={benchmark} populationAverage={averageRisk} onBadgeClick={onBadgeClick} />
            {/* Content below odometer on mobile only */}
            <motion.div
              className="lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              {/* Lab order eligibility banner */}
              {isEligibleForLabOrder && (
                <div className="mb-4 rounded-lg bg-slate-700 p-4">
                  <div className="mb-3 text-center">
                    <p className="flex items-center justify-center gap-2 text-sm font-semibold text-white">
                      <IconAlertCircle color="#F59E0B" size="m" />
                      <span>66% complete · Labs needed</span>
                    </p>
                    <p className="mt-1 text-sm text-gray-300">Your score is incomplete without clinical lab data</p>
                  </div>
                  <button
                    type="button"
                    onClick={onLabOrderClick}
                    className="w-full rounded-md bg-catch-avocado-400 px-4 py-2 text-sm font-semibold text-catch-dark-blue-90 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-catch-avocado-400 focus:ring-offset-2 focus:ring-offset-slate-700"
                    aria-label="Order clinical lab tests"
                  >
                    Order Labs
                  </button>
                </div>
              )}
              <a
                href={methodologyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="focus:ring-opacity-50 cursor-pointer rounded-sm text-[16px] leading-[1.3] font-semibold text-[#7cf1c7] underline transition-opacity hover:opacity-80 focus:ring-2 focus:ring-[#7cf1c7] focus:outline-none"
                aria-label="Learn how your cancer risk is calculated (opens in new tab)"
              >
                Learn how your risk is calculated
              </a>
              <p className="mt-2 text-white">
                <MarkdownTextTailwind>{biologicalAgeText}</MarkdownTextTailwind>
              </p>
            </motion.div>
          </div>

          {/* Right side content - fills remaining space */}
          <div className="order-1 flex flex-1 flex-col justify-center lg:order-2">
            <HeaderTitle
              name={name}
              benchmark={benchmark}
              methodologyUrl={methodologyUrl}
              lifetimeRisk={currentRisk}
              populationAverage={averageRisk}
              userGender={userGender}
              biologicalAgeText={biologicalAgeText}
            />
          </div>
        </div>
      </AnimatedCard>

      {/* Content below the card */}
      {belowCardContent && <div className="mx-auto mt-6 w-[335px]">{belowCardContent}</div>}
    </div>
  );
}
